import { useEffect, useRef, useState } from 'react';
import { useTrack } from '@/analytics';
import { useInstrumentRealtime } from '@/lib/realtime';
import { formatSpot } from '@/lib/snake-market';
import { cn } from '@/lib/utils';
import { LightweightChart } from '../LiveTrading/LightweightChart';
import { BtcLogo } from './BtcLogo';
import { ConfettiBurst } from './ConfettiBurst';
import { useFtueCompletion } from './ftueCompletion';

/** One round of the demo. `resolved` carries the outcome in `result`. */
type Phase = 'call' | 'running' | 'resolved';
type RoundResult = 'win' | 'lose' | 'push';
type Side = 'up' | 'down';

/** How long a practice round runs. Short on purpose — instant feedback teaches. */
const ROUND_SECONDS = 10;
/** The stake and its payout ($5 → $10, the ~2x of a coin-flip call). Simulated. */
const STAKE_DOLLARS = 5;
const WIN_PAYOUT = STAKE_DOLLARS * 2;

/**
 * The `step-by-step` arm of `exp.ftue.experience` — a 10-second Bitcoin
 * Up/Down game (simulated stakes).
 *
 * The PRICE is real: the chart is the production snake dollar chart
 * ({@link LightweightChart}, the same one on the Short Markets lobby card),
 * streaming the live BTC spot itself; the locked price renders as its
 * dashed Target line. The game logic mirrors the same firehose via
 * `useInstrumentRealtime('BTC')` to lock/resolve. The MONEY is not real:
 * the $5 stake is practice, the "order" is client state, and nothing
 * touches trading/monetization/wallet. Pick Up or Down, watch the chart
 * for {@link ROUND_SECONDS} seconds, and the round resolves against the
 * locked price — confetti on a win. The Short Markets widget is this exact
 * game with real money, so the learning transfers 1:1. Per product decision
 * the UI copy does NOT say "practice"/"demo" — but nothing here mints,
 * debits, or persists anything; the scoreboard is session state.
 */
export function StepByStepLobby() {
  const [spot, setSpot] = useState<number | null>(null);
  const [phase, setPhase] = useState<Phase>('call');
  const [side, setSide] = useState<Side | null>(null);
  const [locked, setLocked] = useState<number | null>(null);
  const [result, setResult] = useState<RoundResult | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(ROUND_SECONDS);
  /** Session round log, oldest → newest. Drives the scoreboard + W/L dots. */
  const [history, setHistory] = useState<RoundResult[]>([]);

  // Latest spot, mirrored into a ref so the countdown interval resolves
  // against the freshest tick instead of a stale render closure. (The chart
  // draws itself — this subscription only feeds the game logic + $ label.)
  const spotRef = useRef<number | null>(null);
  useInstrumentRealtime('BTC', (tick) => {
    spotRef.current = tick.lastPrice;
    setSpot(tick.lastPrice);
  });

  // Handler mirrors for the resolver (interval closures go stale).
  const lockedRef = useRef<number | null>(null);
  const sideRef = useRef<Side | null>(null);

  // The round clock. Ticks once a second while running; on zero it resolves
  // against the locked price. State updates happen inside the interval
  // callback (async), never in the effect body.
  useEffect(() => {
    if (phase !== 'running') return;
    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev > 1) return prev - 1;
        clearInterval(interval);
        const end = spotRef.current;
        const start = lockedRef.current;
        const dir = sideRef.current;
        const outcome: RoundResult =
          end == null || start == null || dir == null || end === start
            ? 'push'
            : end > start === (dir === 'up')
              ? 'win'
              : 'lose';
        setPhase('resolved');
        setResult(outcome);
        setHistory((rounds) => [...rounds, outcome]);
        return 0;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase]);

  function makeCall(dir: Side) {
    if (spot == null || phase !== 'call') return;
    setSide(dir);
    setLocked(spot);
    sideRef.current = dir;
    lockedRef.current = spot;
    setSecondsLeft(ROUND_SECONDS);
    setPhase('running');
  }

  const track = useTrack();
  const { markCompleted } = useFtueCompletion();

  /**
   * The FTUE's whole job: hand the player to the real product. Marks the
   * one-shot completion flag (the gate above swaps in the real lobby and
   * never shows the FTUE again) and reports the funnel milestone.
   */
  function exitToRealThing() {
    track('ftue_completed', {
      skipped: false,
      rounds_played: history.length,
      rounds_won: history.filter((r) => r === 'win').length,
    });
    // This component IS the step-by-step arm — the variant is its identity.
    markCompleted({ outcome: 'COMPLETED', variant: 'step-by-step' });
  }

  function goAgain() {
    setSide(null);
    setLocked(null);
    setResult(null);
    sideRef.current = null;
    lockedRef.current = null;
    setSecondsLeft(ROUND_SECONDS);
    setPhase('call');
  }

  const delta = locked != null && spot != null ? spot - locked : null;
  const winning = delta != null && side != null && delta !== 0 ? delta > 0 === (side === 'up') : null;

  // ── Scoreboard (session-scoped, pure derivations of the round log) ──────
  const totalWon = history.filter((r) => r === 'win').length * WIN_PAYOUT;
  let streak = 0;
  for (let i = history.length - 1; i >= 0 && history[i] === 'win'; i--) streak++;
  const winHeadline = streak >= 3 ? `${streak} in a row!` : streak === 2 ? 'Two in a row!' : 'You called it!';

  return (
    <div
      data-testid="ftue-step-by-step"
      // Rendered inside the full-screen FtueShell, which owns centering and
      // scroll — this root only caps the width.
      className="relative w-full max-w-[560px] lg:max-w-[760px] xl:max-w-[880px] mx-auto flex flex-col items-stretch gap-4"
    >
      {/* Game-feel keyframes. One block, all phases; killed wholesale under
          prefers-reduced-motion (transitions/pings are subtle enough to keep). */}
      <style>{`
        @keyframes ftue-pop { 0% { transform: scale(.85); opacity: 0; } 60% { transform: scale(1.06); opacity: 1; } 100% { transform: scale(1); } }
        @keyframes ftue-rise { from { transform: translateY(10px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes ftue-shake { 10%, 90% { transform: translateX(-1px); } 20%, 80% { transform: translateX(2px); } 30%, 50%, 70% { transform: translateX(-4px); } 40%, 60% { transform: translateX(4px); } }
        @keyframes ftue-bob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
        @keyframes ftue-heartbeat { 0%, 100% { transform: scale(1); } 30% { transform: scale(1.35); } }
        .ftue-anim-pop { animation: ftue-pop .45s cubic-bezier(.34,1.56,.64,1) both; }
        .ftue-anim-rise { animation: ftue-rise .35s ease-out both; }
        .ftue-anim-shake { animation: ftue-shake .5s ease-in-out both; }
        .ftue-anim-bob { display: inline-block; animation: ftue-bob 1.2s ease-in-out infinite; }
        .ftue-anim-bob-alt { display: inline-block; animation: ftue-bob 1.2s ease-in-out .6s infinite; }
        .ftue-anim-heartbeat { display: inline-block; animation: ftue-heartbeat 1s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .ftue-anim-pop, .ftue-anim-rise, .ftue-anim-shake, .ftue-anim-bob, .ftue-anim-bob-alt, .ftue-anim-heartbeat { animation: none; }
        }
      `}</style>
      {/* Confetti rains over the WHOLE game on a win, not just the chart. */}
      {phase === 'resolved' && result === 'win' && <ConfettiBurst />}

      {/* key: replay the pop whenever the phase/result headline changes. */}
      <div key={`${phase}-${result ?? ''}`} className="ftue-anim-pop flex flex-col gap-1 text-center">
        <h1
          className={cn(
            'm-0 text-[24px] lg:text-[32px] font-semibold tracking-[-0.48px]',
            phase === 'resolved' && result === 'win' ? 'text-[var(--color-success)]' : 'text-ink',
          )}
        >
          {phase === 'call' && 'Will Bitcoin go UP or DOWN?'}
          {phase === 'running' && `You called ${side === 'up' ? 'UP ▲' : 'DOWN ▼'} — watch it live`}
          {phase === 'resolved' && result === 'win' && winHeadline}
          {phase === 'resolved' && result === 'lose' && 'Not this time…'}
          {phase === 'resolved' && result === 'push' && 'Dead even!'}
        </h1>
        <p className="m-0 text-[14.5px] lg:text-[16px] text-ink-muted">
          {phase === 'call' &&
            `You're watching the real Bitcoin price. Guess where it lands in ${ROUND_SECONDS} seconds — a right guess wins $${WIN_PAYOUT}.`}
          {phase === 'running' && `Finish ${side === 'up' ? 'above' : 'below'} the dotted line and you win $${WIN_PAYOUT}.`}
          {phase === 'resolved' && result === 'win' && 'You just read the market. Now do it for real.'}
          {phase === 'resolved' &&
            result === 'lose' &&
            'So close — Bitcoin dipped the other way. Nothing lost. Run it back.'}
          {phase === 'resolved' && result === 'push' && 'The price landed exactly where you locked it. Call it a warm-up.'}
        </p>
      </div>

      {/* Scoreboard — appears once the first round is in the books. */}
      {history.length > 0 && (
        <div data-testid="ftue-scoreboard" className="ftue-anim-rise flex items-center justify-center gap-4">
          <span className="flex items-baseline gap-1.5 text-[13px] text-ink-muted">
            Won
            <span key={totalWon} className="ftue-anim-pop font-mono text-[15px] font-bold text-ink tabular-nums">
              ${totalWon}
            </span>
          </span>
          <span className="text-ink-muted/40">·</span>
          <span className="flex items-baseline gap-1.5 text-[13px] text-ink-muted">
            Streak
            <span
              key={streak}
              data-testid="ftue-streak"
              className={cn(
                'ftue-anim-pop font-mono text-[15px] font-bold tabular-nums',
                streak > 0 ? 'text-purple' : 'text-ink',
              )}
            >
              {streak > 0 ? streak : '—'}
            </span>
          </span>
          <span className="text-ink-muted/40">·</span>
          <span className="flex items-center gap-1" data-testid="ftue-round-dots" aria-label="Round history">
            {history.slice(-8).map((r, i) => (
              <span
                key={`${history.length}-${i}`}
                className={cn(
                  'ftue-anim-pop inline-block size-2.5 rounded-full',
                  r === 'win' && 'bg-[var(--color-success)]',
                  r === 'lose' && 'bg-[var(--color-danger)]',
                  r === 'push' && 'bg-ink-muted/40',
                )}
              />
            ))}
          </span>
        </div>
      )}

      {/* The live chart card — border breathes with the round: green while the
          call is winning, red while losing, shakes once on a lost round. */}
      <div
        className={cn(
          'relative rounded-xl border bg-surface shadow-card p-4 flex flex-col gap-3 transition-colors duration-300',
          result === 'lose' && 'ftue-anim-shake',
          (phase === 'running' || phase === 'resolved') && winning === true && 'border-[var(--color-success)]',
          (phase === 'running' || phase === 'resolved') && winning === false && 'border-[var(--color-danger)]',
          (phase === 'call' || winning == null) && 'border-border',
        )}
      >
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-[13px] lg:text-[15px] font-semibold text-ink">
            <BtcLogo className="size-6 lg:size-8" />
            Bitcoin · live
            <span className="relative flex size-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--color-danger)] opacity-75" />
              <span className="relative inline-flex size-2 rounded-full bg-[var(--color-danger)]" />
            </span>
          </span>
          <span
            className="font-mono text-[15px] lg:text-[18px] font-semibold text-ink tabular-nums"
            data-testid="ftue-btc-spot"
          >
            {formatSpot(spot)}
          </span>
        </div>

        {/* The production snake dollar chart (Short Markets lobby card), with
            the locked price as its dashed Target line. Display-only here —
            taps must reach the buttons, not pan the chart. */}
        <div data-testid="ftue-btc-chart" className="relative h-[220px] lg:h-[320px] xl:h-[380px] w-full">
          <LightweightChart instrumentSymbol="BTC" targetPrice={locked} interactive={false} />
          {/* Final-seconds drama: a big 3…2…1 pops over the chart. */}
          {phase === 'running' && secondsLeft <= 3 && secondsLeft > 0 && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
              <span
                key={secondsLeft}
                data-testid="ftue-final-countdown"
                className="ftue-anim-pop text-[72px] lg:text-[104px] font-bold leading-none text-purple/70 drop-shadow-sm"
              >
                {secondsLeft}
              </span>
            </div>
          )}
        </div>

        {phase === 'running' && (
          <div className="ftue-anim-rise flex flex-col gap-1.5" data-testid="ftue-countdown">
            <div className="flex items-center justify-between text-[13px]">
              <span className="text-ink-muted">
                Your line: <span className="font-mono font-semibold text-ink tabular-nums">{formatSpot(locked)}</span>
              </span>
              {/* key: pop when the call flips between winning and losing. */}
              <span
                key={String(winning)}
                className={cn(
                  'ftue-anim-pop font-mono font-semibold tabular-nums',
                  winning == null
                    ? 'text-ink-muted'
                    : winning
                      ? 'text-[var(--color-success)]'
                      : 'text-[var(--color-danger)]',
                )}
              >
                {delta == null || delta === 0 ? '±$0.00' : `${delta > 0 ? '+' : '−'}$${Math.abs(delta).toFixed(2)}`}
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-pill bg-chip-purple">
              <div
                className={cn(
                  'h-full rounded-pill transition-[width,background-color] duration-1000 ease-linear',
                  secondsLeft <= 3 ? 'bg-[var(--color-danger)]' : 'bg-purple',
                )}
                style={{ width: `${(secondsLeft / ROUND_SECONDS) * 100}%` }}
              />
            </div>
            <p className="m-0 text-center text-[13px] text-ink-muted">
              Resolves in{' '}
              <span
                className={cn(
                  'font-semibold tabular-nums',
                  secondsLeft <= 3 ? 'ftue-anim-heartbeat text-[15px] text-[var(--color-danger)]' : 'text-ink',
                )}
              >
                {secondsLeft}s
              </span>
            </p>
          </div>
        )}
      </div>

      {phase === 'call' && (
        <div className="ftue-anim-rise flex flex-col gap-2">
          {/* Same gradient-pill trade buttons as the Short Markets bar
              (SnakeTradeBar), percent included — a fresh 10-second round
              against your own line is a coin flip, so both sides read 50%. */}
          <div className="flex gap-2.5">
            <button
              data-testid="ftue-btc-up"
              onClick={() => makeCall('up')}
              disabled={spot == null}
              className="bg-gradient-up shadow-btn-strong flex h-[54px] lg:h-[62px] flex-1 items-center justify-center gap-1.5 rounded-[51px] border-none text-white cursor-pointer transition-transform duration-150 hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-default disabled:hover:scale-100"
            >
              <span className="ftue-anim-bob text-[15px]" aria-hidden>
                ▲
              </span>
              <span className="text-[20px] lg:text-[22px] font-semibold">Up</span>
              <span className="text-[14px] lg:text-[15px] font-semibold">50%</span>
            </button>
            <button
              data-testid="ftue-btc-down"
              onClick={() => makeCall('down')}
              disabled={spot == null}
              className="bg-gradient-down shadow-btn-strong flex h-[54px] lg:h-[62px] flex-1 items-center justify-center gap-1.5 rounded-[51px] border-none text-white cursor-pointer transition-transform duration-150 hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-default disabled:hover:scale-100"
            >
              <span className="ftue-anim-bob-alt text-[15px]" aria-hidden>
                ▼
              </span>
              <span className="text-[20px] lg:text-[22px] font-semibold">Down</span>
              <span className="text-[14px] lg:text-[15px] font-semibold">50%</span>
            </button>
          </div>
          <p className="m-0 text-center text-[13px] text-ink-muted">
            {spot == null ? (
              <span data-testid="ftue-connecting">Connecting to the live Bitcoin feed…</span>
            ) : (
              <>Tap a side — the price locks the moment you do</>
            )}
          </p>
        </div>
      )}

      {phase === 'resolved' && (
        <div className="ftue-anim-rise flex flex-col items-center gap-3" data-testid={`ftue-result-${result}`}>
          {result === 'win' && (
            <span className="ftue-anim-pop rounded-pill bg-[var(--color-success)] px-6 py-2 text-[20px] lg:text-[24px] font-bold text-white shadow-lg">
              +${WIN_PAYOUT}
            </span>
          )}
          {/* The exit to the product is the hero action; replay is secondary. */}
          <div className="flex w-full items-stretch gap-2.5">
            <button
              data-testid="ftue-go-again"
              onClick={goAgain}
              className="flex-1 rounded-pill border border-border bg-surface px-4 py-3.5 text-[15px] font-semibold text-ink cursor-pointer transition-transform duration-150 hover:scale-[1.02] active:scale-95"
            >
              Play again
            </button>
            <button
              data-testid="ftue-exit"
              onClick={exitToRealThing}
              className="flex-[1.8] rounded-pill bg-purple px-6 py-3.5 text-[17px] lg:text-[19px] font-bold text-white border-none cursor-pointer transition-transform duration-150 hover:scale-[1.03] active:scale-95 shadow-md"
            >
              Take me to the real thing
            </button>
          </div>
          <p className="m-0 text-center text-[13px] text-ink-muted">
            Real calls work exactly like this — and the winnings are real too.
          </p>
        </div>
      )}
    </div>
  );
}
